import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import toast from 'react-hot-toast'

export default function TimesheetList() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const [loading, setLoading] = useState(true)
    const [timesheets, setTimesheets] = useState([])
    const [search, setSearch] = useState('')

    // Check if user can see all timesheets
    const canSeeAll = user?.canSheets || user?.isSysAdmin || user?.isAdmin

    useEffect(() => {
        loadTimesheets()
    }, [])

    async function loadTimesheets() {
        setLoading(true)
        try {
            // If canSeeAll, get all drafts; otherwise get only user's drafts
            let url = '/api/timesheets?status=0'
            if (!canSeeAll && user?.id) {
                url += `&userId=${user.id}`
            }

            const response = await api.get(url)
            console.log('Timesheets response:', response)
            if (response.success) {
                setTimesheets(response.timesheets || [])
            } else {
                toast.error('Failed to load timesheets')
            }
        } catch (error) {
            console.error('Error:', error)
            toast.error('Error loading timesheets')
        }
        setLoading(false)
    }

    const filteredTimesheets = timesheets.filter(ts =>
        (ts.workOrderNumber || '').toLowerCase().includes(search.toLowerCase()) ||
        (ts.customer || '').toLowerCase().includes(search.toLowerCase()) ||
        (ts.referenceNo || '').toLowerCase().includes(search.toLowerCase()) ||
        (ts.employeeName || '').toLowerCase().includes(search.toLowerCase())
    )

    const getTypeLabel = (type) => {
        switch (type) {
            case '0': return 'Timesheet'
            case '1': return 'Repair in Shop'
            case '2': return 'Packing Slip'
            case '3': return 'Internal'
            case '4': return 'Pickup Slip'
            case '5': return 'Vendor Slip'
            default: return 'Timesheet'
        }
    }

    const getTypeColor = (type) => {
        switch (type) {
            case '0': return 'bg-blue-100 text-blue-700'
            case '1': return 'bg-purple-100 text-purple-700'
            case '2': return 'bg-green-100 text-green-700'
            case '3': return 'bg-gray-100 text-gray-700'
            case '4': return 'bg-orange-100 text-orange-700'
            default: return 'bg-blue-100 text-blue-700'
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner w-8 h-8"></div>
            </div>
        )
    }

    return (
        <div>
            {/* Header */}
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Load Timesheet</h1>
                    <p className="text-blue-100 mt-1">
                        {filteredTimesheets.length} draft timesheet{filteredTimesheets.length !== 1 ? 's' : ''}
                        {canSeeAll && ' (all users)'}
                    </p>
                </div>
                <Link to="/create-timesheet">
                    <Button>+ New Timesheet</Button>
                </Link>
            </div>

            {/* Search */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                <Input
                    placeholder={canSeeAll
                        ? "Search by job number, customer, reference, or employee..."
                        : "Search by job number, customer, or reference..."}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {/* List */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                {filteredTimesheets.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <p className="text-4xl mb-2">📝</p>
                        <p>No draft timesheets</p>
                        <p className="text-sm mt-2">
                            {canSeeAll ? 'No unsubmitted timesheets found' : 'All your timesheets have been submitted'}
                        </p>
                        <Link to="/create-timesheet">
                            <Button variant="outline" className="mt-4">
                                Create New Timesheet
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="divide-y">
                        {filteredTimesheets.map((ts) => (
                            <Link
                                key={ts.uuid || ts.id}
                                to={`/timesheet/${ts.uuid}`}
                                className="block p-4 hover:bg-blue-50 transition-colors"
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="font-semibold text-gray-800 text-lg">
                                                {ts.customer || 'No Customer'}
                                            </p>
                                            <span className={`text-xs px-2 py-0.5 rounded ${getTypeColor(ts.tsType)}`}>
                                                {getTypeLabel(ts.tsType)}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {ts.referenceNo && <span className="mr-3">Ref: {ts.referenceNo}</span>}
                                            {ts.month && <span className="mr-3">📅 {ts.month}</span>}
                                            {ts.employeeName && <span>👤 {ts.employeeName}</span>}
                                        </p>
                                        {ts.jobSite && (
                                            <p className="text-sm text-gray-400 mt-1">📍 {ts.jobSite}</p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700">
                                            Draft
                                        </span>
                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}